/**
 * In-app APK self-update — JS-side helper for the native `NativePlayer`
 * plugin methods `downloadApk` / `cancelApkDownload` / `installApk`.
 *
 * Flow:
 *   1. `startApkDownload({url, onProgress, onDone, onError})` kicks off
 *      a native HTTP stream into the app's cache dir. We listen on the
 *      plugin events `apkDownloadProgress`, `apkDownloadDone` and
 *      `apkDownloadError`.
 *   2. When `onDone` fires we know the file is on disk. The caller then
 *      invokes `installDownloadedApk(path)` which fires the system
 *      package installer Intent (FileProvider-backed).
 *
 * On non-Android hosts (Electron desktop, plain browsers) the helpers
 * resolve / call the error callback synchronously — the UI then falls
 * back to a plain "open in browser" link.
 */
import { isAndroid } from "./deviceId";

let plugin = null;
function getPlugin() {
  if (plugin) return plugin;
  if (!isAndroid()) return null;
  const Capacitor = window.Capacitor;
  if (!Capacitor) return null;
  try {
    plugin = Capacitor.Plugins?.NativePlayer || null;
  } catch {
    plugin = null;
  }
  return plugin;
}

/** True when we can actually do an in-app download + install. */
export function canInstallApkInApp() {
  return !!getPlugin();
}

let _progressUnsub = null;
let _doneUnsub = null;
let _errorUnsub = null;

function teardownListeners() {
  try { _progressUnsub?.remove?.(); } catch { /* noop */ }
  try { _doneUnsub?.remove?.(); } catch { /* noop */ }
  try { _errorUnsub?.remove?.(); } catch { /* noop */ }
  _progressUnsub = _doneUnsub = _errorUnsub = null;
}

/**
 * Start a native APK download. Resolves with the destination path
 * almost immediately (the actual transfer is async — listen on the
 * onProgress / onDone / onError callbacks for state).
 */
export async function startApkDownload({ url, onProgress, onDone, onError } = {}) {
  const p = getPlugin();
  if (!p) {
    const err = new Error("Native installer not available on this platform.");
    onError?.(err);
    throw err;
  }
  if (!url) throw new Error("Missing url");

  teardownListeners();
  _progressUnsub = await p.addListener("apkDownloadProgress", (ev) => {
    onProgress?.(ev);
  });
  _doneUnsub = await p.addListener("apkDownloadDone", (ev) => {
    teardownListeners();
    onDone?.(ev);
  });
  _errorUnsub = await p.addListener("apkDownloadError", (ev) => {
    teardownListeners();
    onError?.(new Error(ev?.message || "Download failed"));
  });

  const res = await p.downloadApk({ url });
  return res; // { ok: true, path: "/.../updates/za-player-pro-latest.apk" }
}

/** Cancel an in-flight download. */
export async function cancelApkDownload() {
  const p = getPlugin();
  if (!p) return;
  try {
    await p.cancelApkDownload();
  } catch {
    /* noop */
  }
}

/** Launch the system package installer for a previously-downloaded APK. */
export async function installDownloadedApk(path) {
  const p = getPlugin();
  if (!p) throw new Error("Native installer not available on this platform.");
  return p.installApk({ path: path || "" });
}
